import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { ProductCatalogItem } from "@ozon-ai/ozon/contracts";
import { useGridCellEditor, type CustomCellEditorProps } from 'ag-grid-react';
import { Input } from '@/components/ui/input.tsx';
import { CatalogItemHoverPreview, CatalogItemThumbnail } from './catalog-item-hover-preview';
import { ScrollArea } from '@/components/ui/scroll-area.tsx';
import type { PurchaseGridRow } from './OfferIdCellRenderer';
import { searchCatalog } from './offerCatalogSearch';

interface OfferIdEditorParams {
  catalog: ProductCatalogItem[];
  onProductSelect?: (rowId: string, item: ProductCatalogItem | null, freeText: string) => void;
}

export function OfferIdCellEditor(
  props: CustomCellEditorProps<PurchaseGridRow, string> & OfferIdEditorParams
) {
  const { catalog, onProductSelect, data, stopEditing } = props;
  const [query, setQuery] = useState(props.value || '');
  const inputRef = useRef<HTMLInputElement>(null);

  const filtered = useMemo(() => searchCatalog(catalog, query), [catalog, query]);

  useGridCellEditor({
    focusIn() {
      inputRef.current?.focus();
    },
  });

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  function pickItem(item: ProductCatalogItem) {
    setQuery(item.offerId);
    props.onValueChange(item.offerId);
    onProductSelect?.(data?.id || '', item, item.offerId);
    stopEditing();
  }

  function applyFreeText() {
    const value = query.trim();
    props.onValueChange(value);
    onProductSelect?.(data?.id || '', null, value);
    stopEditing();
  }

  function handleSearchKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Enter') {
      event.preventDefault();
      if (filtered.length === 1) {
        pickItem(filtered[0]);
      } else {
        applyFreeText();
      }
    }
    if (event.key === 'Escape') {
      stopEditing(true);
    }
  }

  return (
    <div
      className="z-[10000] w-[min(360px,92vw)] rounded-lg border border-border bg-popover p-2 text-popover-foreground shadow-md"
      onMouseDown={(event) => event.stopPropagation()}
    >
      <Input
        ref={inputRef}
        className="mb-2"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={handleSearchKeyDown}
        placeholder="Поиск по offer_id, названию, SKU (с опечатками)…"
      />
      <ScrollArea className="h-60">
        <div className="flex flex-col gap-0.5 pr-2">
          {filtered.map((item) => (
            <CatalogSearchResultRow key={item.offerId} item={item} onPick={() => pickItem(item)} />
          ))}
          {filtered.length === 0 && (
            <p className="p-2 text-xs text-muted-foreground">
              Ничего не найдено — Enter для ручного ввода
            </p>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

function CatalogSearchResultRow({
  item,
  onPick,
}: {
  item: ProductCatalogItem;
  onPick: () => void;
}) {
  const subtitle = `${item.offerId}${item.ozonSku ? ` · SKU ${item.ozonSku}` : ''}`;

  return (
    <CatalogItemHoverPreview
      item={item}
      title={item.name}
      triggerRender={
        <button
          type="button"
          className="flex w-full items-center gap-2 rounded-md p-1 text-left hover:bg-muted"
          onClick={onPick}
        />
      }
    >
      <CatalogItemThumbnail item={item} title={item.name} subtitle={subtitle} />
    </CatalogItemHoverPreview>
  );
}
